import axios from 'axios';
import MockAdapter from 'axios-mock-adapter';
import { h } from 'vue';
import { createDiscreteApi } from 'naive-ui';
import { throttle } from 'lodash';

import { getSession } from '@/utils/storage';

import store from '../store';
import { setToken, removeToken } from './system';
import { removeStroage } from './storage';
import mockInterceptors from '../mock/index';
import { jumpLogin } from './system';

import type { AxiosRequestConfig } from 'axios';

const isMock = import.meta.env.VITE_GLOB_MOCK;

const { message } = createDiscreteApi(['message']);

const dbMessageError = throttle(message.error, 600);
const dbMessageWarning = throttle(message.warning, 600);

// 可以在这里手动注释
if (isMock) {
  const mock = new MockAdapter(axios);
  mockInterceptors(mock);
}

export interface RequestOptions {
  /** 当前接口权限, 不需要鉴权的接口请忽略， 格式：sys:user:add */
  permCode?: string;
  /** 是否直接获取data，而忽略message等 */
  isGetDataDirectly?: boolean;
  /** 请求成功是提示信息 */
  successMsg?: string;
  /** 请求失败是提示信息 */
  errorMsg?: string;
  /** 是否mock数据请求 */
  isMock?: boolean;
}

const baseApiUrl: string | undefined = import.meta.env.VITE_GLOB_API_URL;
// const didlogoutUrl = import.meta.env.VITE_GLOB_DID_LOGOUT_URL;
const didloginUrl = import.meta.env.VITE_GLOB_DID_URL;
// VITE_SEVERLESS_URL
const serverlessUrl: string | undefined = import.meta.env.VITE_SEVERLESS_URL;
// VITE_GLOB_API_URL_TEST
const testEnvUrl: string | undefined = import.meta.env.VITE_GLOB_API_URL_TEST;
// console.log('>>>>>>>>>>>', didloginUrl);

const service = axios.create({
  timeout: 20 * 1000,
});

const UNKNOWN_ERROR = '未知错误，请重试';
const WARNNING_ERROR = '提示警告信息';

const pending: any = [];
const cancelPending = (key: any, isRequest = false) => {
  // 每次发起请求的时候，判断之前有没有在pending的相同请求，如果有就取消在pending的请求
  if (pending[key] && isRequest) {
    pending[key]('取消重复请求');
  }

  // 从队列里删除上一次的重复请求记录
  delete pending[key];
};

const getRequestIdentify = (config: any) => {
  return config.method === 'get'
    ? config.url + (typeof config.params === 'object' ? JSON.stringify(config.params) : config.params)
    : config.url + (typeof config.data === 'object' ? JSON.stringify(config.data) : config.data);
};

service.interceptors.request.use(
  (config: any) => {
    const requestData = getRequestIdentify(config);
    // 拦截重复请求(即当前正在pending的相同请求)
    // cancelPending(requestData, true);
    // 判断是否存在token，如果存在的话，则每个http header都加上token
    const token = store.getters.token;
    if (token) {
      config.headers['X-Token'] = token;
    }
    // 数据格式
    config.headers.Accept = 'application/json';
    // 每一个请求new一个token，然后存在store里面
    config.cancelToken = new axios.CancelToken((cancel) => {
      store.dispatch('http/saveRequestQueue', cancel);
      // pending.push({ requestData: cancel })
      pending[requestData] = cancel;
    });
    // console.log(config.url, config.method);
    return config;
  },
  (error) => {
    Promise.reject(new Error(error));
  }
);

service.interceptors.response.use(
  async (response: any) => {
    if (isMock) {
      try {
        console.log(
          '>>>>>>>>>>>>>>>>>>>>>mock',
          response.request.responseURL,
          'payload:',
          response.config.data ? JSON.parse(response.config.data) : response.config.data,
          'response:',
          response.data
        );
      } catch (e) {
        console.log(e);
      }
    }

    // 把已经完成的请求从 pending 中移除；因为请求已经得到响应，所以不需要去取消请求)
    const requestData = getRequestIdentify(response.config);
    cancelPending(requestData);
    if (response?.headers?.newtoken) {
      removeToken();
      setToken(response?.headers?.newtoken);
      store.dispatch('user/loginByToken', response.headers.newtoken);
    }
    if (response?.headers?.['content-type'] && response?.headers?.['content-type'] == 'application/octet-stream') {
      return Promise.resolve(response.data);
    }
    let res = response?.data;
    try {
      if (res instanceof Blob) {
        const text = await res.text();
        res = JSON.parse(text);
      }
    } catch (e) {
      console.log(e);
    }
    // if the custom code is not 200, it is judged as an error.
    if (res.status === '0') {
      return res.data;
    } else {
      // 不管任何code 取消全局提示的报错可以用cancelError这个参数
      if (response.config.cancelError) {
        return Promise.reject(new Error(UNKNOWN_ERROR));
      }

      if (res.status === '300') {
        // window.$message.error(res.msg.replace(/;/g, ';\n'), {
        //   closable: true,
        //   duration: 0,
        // });
        window.$dialog.error({
          title: 'Error',
          type: 'error',
          class: 'error-dialog',
          content: () =>
            h(
              'div',
              {},
              res.msg.split(';').map((str: string) => {
                return h('p', {}, str);
              })
            ),
          positiveText: 'OK',
          closable: false,
          maskClosable: false,
          onPositiveClick: () => {
            // window.location.href = location.origin;
          },
        });
        return Promise.reject(new Error(WARNNING_ERROR));
      } else if (res.status === '406') {
        dbMessageError('The user does not exist, please contact the administratonr', {
          duration: 20 * 1000,
        });
        // window.location.href = "https://iam-saml-uat.bba-app.com/ms_oauth/oauth2/endpoints/oauthservice/authorize?client_id=04c7291a0c9a47a0b453ec74cfb643c7&response_type=code&redirect_uri=http://edm.bmw.com.cn&scope=UserProfile.me&state=xyz";
      } else if (['401', '403', '405'].includes(res.status)) {
        removeStroage('userInfo');
        removeToken();
        dbMessageError('Please login again');
        // 跳转登录
        jumpLogin(didloginUrl);
      } else if (res.status === '409') {
        // Login failed, please contact emdOps: EDMOps.Service@bmw-brilliance.cn!
        window.$dialog.warning({
          title: 'Warning',
          content: () => {
            return [
              'Login failed, please contact EDMOps team: ',
              h(
                'a',
                {
                  href: 'mailto:EDMOps.Service@bmw-brilliance.cn',
                },
                {
                  default: () => `EDMOps.Service@bmw-brilliance.cn`,
                }
              ),
              '!',
            ];
          },
          positiveText: 'Send email',
          maskClosable: false,
          style: {
            width: '657px',
          },
          onPositiveClick: () => {
            window.location.href = 'mailto:EDMOps.Service@bmw-brilliance.cn';
          },
        });
      } else if (res.status === '601') {
        window.$dialog.warning({
          title: 'Warning',
          content: `You don't have permission for this page.`,
          positiveText: 'OK',
          closable: false,
          maskClosable: false,
          onPositiveClick: () => {
            window.location.href = location.origin;
          },
        });
      } else if (res.status === '6000') {
        dbMessageWarning(res.msg, {
          closable: true,
          duration: 0,
        });
        return Promise.reject(new Error(WARNNING_ERROR));
      } else if (res.status === '7000') {
        window.$dialog.warning({
          title: 'Tips',
          content: res.msg,
          positiveText: 'OK',
          closable: false,
          maskClosable: false,
          class: 'warning-dialog',
        });
        return Promise.reject(new Error(WARNNING_ERROR));
      }
      dbMessageError(res.msg, {
        closable: true,
        duration: 0,
      });
      return Promise.reject(new Error(UNKNOWN_ERROR));
    }
  },
  (error: any) => {
    if (error.message !== '取消重复请求') {
      dbMessageError('Network Error', {
        keepAliveOnHover: true,
      });
    }

    // 处理 422 或者 500 的错误异常提示
    const errMsg = error?.message || UNKNOWN_ERROR;
    // $message.error(errMsg);
    // error.message = errMsg;
    return Promise.reject(new Error(errMsg));
  }
);

/**
 *
 * @param method - request methods
 * @param url - request url
 * @param data - request data or params
 */
export const request = async (config: AxiosRequestConfig | any): Promise<any> => {
  try {
    // const { successMsg, errorMsg, permCode, isMock, isGetDataDirectly = true } = options;
    const userInfo = getSession('userInfo');
    const { testenv } = config;
    // console.log('tets+++', testenv);
    const { serverless } = JSON.parse(userInfo || '{}');

    let requestUrl = config.serverless && !serverless ? serverlessUrl : baseApiUrl;
    if (testenv) {
      requestUrl = testEnvUrl;
    }
    const fullUrl = `${requestUrl + config.url}`;
    config.url = fullUrl.replace(/(?<!:)\/{2,}/g, '/');

    const res = await service.request(config);
    // successMsg && $message.success(successMsg);
    // errorMsg && $message.error(errorMsg);
    return Promise.resolve(res);
  } catch (error: any) {
    return Promise.reject(new Error(error));
  }
};
